import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type { CollaboratorListResponse, AlbumCollaborator } from '@omokage/shared';
import { albumKeys } from './useAlbums';

/**
 * Query key factory for collaborator queries
 */
export const collaboratorKeys = {
  all: ['collaborators'] as const,
  lists: () => [...collaboratorKeys.all, 'list'] as const,
  list: (albumId: string) => [...collaboratorKeys.lists(), albumId] as const,
};

/**
 * Hook to fetch collaborators for a specific album
 *
 * @param albumId - The album ID to fetch collaborators for
 * @returns Query result with array of collaborators
 */
export function useCollaborators(albumId: string) {
  return useQuery({
    queryKey: collaboratorKeys.list(albumId),
    queryFn: async () => {
      const response = await apiClient.get<CollaboratorListResponse>(
        `/albums/${albumId}/collaborators`
      );
      return response.collaborators;
    },
    enabled: !!albumId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to remove a collaborator from an album (owner only)
 *
 * @returns Mutation function to remove a collaborator
 */
export function useRemoveCollaborator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ albumId, userId }: { albumId: string; userId: string }) => {
      await apiClient.delete<{ success: true; message: string }>(
        `/albums/${albumId}/collaborators/${userId}`
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate collaborators list
      queryClient.invalidateQueries({
        queryKey: collaboratorKeys.list(variables.albumId),
      });
      // Invalidate album detail to update metadata
      queryClient.invalidateQueries({
        queryKey: albumKeys.detail(variables.albumId),
      });
    },
  });
}

/**
 * Hook to leave an album (non-owner collaborators)
 *
 * @returns Mutation function to leave an album
 */
export function useLeaveAlbum() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (albumId: string) => {
      await apiClient.post<{ success: true; message: string }>(
        `/albums/${albumId}/collaborators/leave`
      );
    },
    onSuccess: (_, albumId) => {
      // Invalidate collaborators list
      queryClient.invalidateQueries({
        queryKey: collaboratorKeys.list(albumId),
      });
      // Invalidate album lists to remove the album from user's list
      queryClient.invalidateQueries({
        queryKey: albumKeys.lists(),
      });
      // Remove album detail from cache
      queryClient.removeQueries({
        queryKey: albumKeys.detail(albumId),
      });
    },
  });
}

/**
 * Get the current user's role in an album
 *
 * @param collaborators - Array of album collaborators
 * @param currentUserId - Current user's ID
 * @returns The user's role or null if not found
 */
export function getUserRole(
  collaborators: AlbumCollaborator[] | undefined,
  currentUserId: string | undefined
): 'owner' | 'contributor' | null {
  if (!collaborators || !currentUserId) return null;
  const userCollab = collaborators.find((c) => c.userId === currentUserId);
  return userCollab?.role || null;
}

/**
 * Check if the current user is the album owner
 *
 * @param collaborators - Array of album collaborators
 * @param currentUserId - Current user's ID
 * @returns True if user is owner
 */
export function isAlbumOwner(
  collaborators: AlbumCollaborator[] | undefined,
  currentUserId: string | undefined
): boolean {
  return getUserRole(collaborators, currentUserId) === 'owner';
}
